/**
 * authedFetch — the fetch-side twin of the axios token-refresh interceptor.
 *
 * Drop-in replacement for `fetch` on same-origin /api calls: attaches the
 * CURRENT access token per call (never a captured/stale one), and on a
 * gateway-auth-shaped 401 runs the shared single-flight refresh and retries
 * once. A dead refresh broadcasts session-expired (handled by AuthContext →
 * soft bounce to the sign-in sheet). Non-auth 401s (proxied API-key
 * failures) pass through untouched.
 *
 * Callers keep full control of the Response — this never throws on HTTP
 * error statuses (matching fetch semantics), only on network failure.
 */

import { getStoredToken } from './authUtils';
import {
  getAccessTokenRemainingMs,
  isAuthShaped401,
  isSessionEndpoint,
  refreshAccessToken,
  TOKEN_REFRESH_MARGIN_MS,
} from './tokenRefreshInterceptor';

const withAuth = (init: RequestInit | undefined, token: string | null): RequestInit => {
  const headers = new Headers(init?.headers || {});
  // Always override — a caller-supplied header may carry a stale captured
  // token (the exact bug class this module exists to end)
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

export const authedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

  // Proactive: refresh BEFORE sending when the token is about to expire —
  // otherwise the first call after expiry goes out doomed and the browser
  // red-logs a 401 even though the retry succeeds (pure console noise).
  if (!isSessionEndpoint(url)) {
    const remaining = getAccessTokenRemainingMs();
    if (remaining !== null && remaining < TOKEN_REFRESH_MARGIN_MS && localStorage.getItem('refresh_token')) {
      try { await refreshAccessToken(); } catch { /* reactive path owns failure */ }
    }
  }

  const response = await fetch(input, withAuth(init, getStoredToken()));

  if (response.status !== 401 || isSessionEndpoint(url)) {
    return response;
  }

  // Classify before refreshing — clone so an unrefreshable 401 still hands
  // the caller a readable body.
  let detail: unknown;
  try {
    detail = (await response.clone().json())?.detail;
  } catch {
    detail = undefined;
  }
  if (!isAuthShaped401(response.headers.get('www-authenticate'), detail)) {
    return response;
  }

  let newToken: string;
  try {
    newToken = await refreshAccessToken();
  } catch {
    // Expiry (when definitive) was already broadcast by the refresher;
    // give the caller the original 401.
    return response;
  }

  return fetch(input, withAuth(init, newToken));
};

export default authedFetch;
